import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Route, Routes, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ProjectDetail from "./pages/ProjectDetail";
import MyProjects from "./pages/MyProjects";
import VersionedFile from "./pages/VersionedFile";

import ExternalShare from "./pages/external/ExternalShare";
import ResponsiveLayout from "./pages/Layout";
import Stats from "./pages/admin/Dashboard";
import SharingLinks from "./pages/SharingLinks";
import { TenantConfigContext } from "./context/tenantConfigContext";
import { useGetTenantConfig } from "./api/tenant/getTenantConfig";
import { useIDPContext } from '@copyrightdelta/drx-frontend-api';
import { LoginRedirect } from "./pages/LoginRedirect";
import { API_ENDPOINT, THEME } from "./Settings";
import { setProfileIdInHeader, setOnProfileRejected, openProfileGate } from "./api/base";
import { fetchProfilesFromIdp, DrxProfile } from "./api/profiles";
import { ProfileContext } from "./context/profileContext";
import ErrorAlert from "./components/Snackbar/ErrorAlert";


const RedirectToHome: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate("/");
  });

  return <></>;
}

const App: React.FC = () => {
  const { user, token, switchProfile, selectedProfile, profiles, idpReady } = useIDPContext()
  const { t } = useTranslation();

  const [freshProfiles, setFreshProfiles] = useState<DrxProfile[]>([])
  const [reconciledProfileId, setReconciledProfileId] = useState<string | null>(null)
  const [profilesReady, setProfilesReady] = useState(false)
  const [removedProfile, setRemovedProfile] = useState<{ gone: string, fallback: string } | null>(null)
  const reconciledToken = useRef<string | null>(null)

  const { data: tenantConfig } = useGetTenantConfig({ config: { enabled: !!THEME && !!API_ENDPOINT, } });

  // Reconcile the profile the user last picked (localStorage — their intent)
  // against the profiles the IDP says they actually have (the authority).
  //
  // This has to re-fetch rather than read useIDPContext()'s `profiles`: on a
  // page refresh drx-frontend-api rehydrates that list from localStorage and
  // never re-checks it, so validating the stored id against it just compares two
  // halves of the same stale snapshot and always agrees. Only the IDP knows
  // whether a profile still exists.
  const reconcileProfiles = async () => {
    try {
      await reconcileProfilesInner()
    } finally {
      // Whatever happened — reconciled, IDP down, visitor with no profiles —
      // requests must not stay blocked.
      openProfileGate()
    }
  }

  const reconcileProfilesInner = async () => {
    const fresh = await fetchProfilesFromIdp()
    // IDP unreachable, or no profiles to choose between: leave existing state be.
    if (!fresh?.length) return

    const validIds = fresh.map((p) => String(p.profile_id))
    const stored = localStorage.getItem('selectedProfileId')
    const hasStored = !!stored && stored !== 'undefined'

    let target: string
    if (hasStored && validIds.includes(stored as string)) {
      target = stored as string
    } else if (!hasStored && selectedProfile && validIds.includes(String(selectedProfile))) {
      // Nothing stored yet — seed from the IDP default. ProfileSelect may never
      // mount to do this (mobile).
      target = String(selectedProfile)
    } else {
      target = validIds[0]
      if (hasStored) {
        // Their selection was revoked in the Hub. Fall back, but say so rather
        // than silently acting as a different profile.
        const goneName = profiles?.find((p) => String(p.profile_id) === stored)?.display_name
        setRemovedProfile({
          gone: goneName || (stored as string),
          fallback: fresh[0]?.display_name || target,
        })
      }
    }

    localStorage.setItem('selectedProfileId', target)
    setProfileIdInHeader()
    setFreshProfiles(fresh)
    setReconciledProfileId(target)
    setProfilesReady(true)

    // Re-mint the token so its profile_id agrees with the header we now send.
    // Best effort: switchProfile validates against the IDP context's stale list,
    // so it no-ops for a profile added since that cache was written. Access is
    // unaffected either way — the backend authorises on the Cpd-Profile-Id
    // header, which is correct by this point, and ignores the token's profile_id.
    if (String(selectedProfile) !== target) {
      await switchProfile(target)
    }
  }

  useEffect(() => {
    if (!token) {
      reconciledToken.current = null
      // No token yet does not mean no session: it is also true on the very
      // first render, and for the whole OAuth code exchange after login. Open
      // the gate only when nothing is coming — no stored token to rehydrate and
      // no login in flight — so public share routes are not made to wait, but a
      // session about to arrive still gets its profile reconciled first.
      const sessionIncoming =
        !!localStorage.getItem('drx-identity-token') ||
        new URLSearchParams(window.location.search).has('code')
      if (idpReady && !sessionIncoming) openProfileGate()
      return
    }
    // Once per token: switchProfile below mints a new one, which re-runs this
    // and settles on the second pass.
    if (reconciledToken.current === token) return
    reconciledToken.current = token
    reconcileProfiles()
  }, [token, idpReady])

  // A profile can also be revoked while the tab is open, which no boot-time
  // check can catch. The backend tells us with profile_not_member; re-reconcile
  // rather than dumping the user at the login screen.
  // Re-registered each render so the handler closes over current IDP state.
  useEffect(() => {
    setOnProfileRejected(async () => {
      reconciledToken.current = null
      await reconcileProfiles()
    })
  })


  return (
    <>
      <ErrorAlert
        open={!!removedProfile}
        onClose={() => setRemovedProfile(null)}
        severity="warning"
        autoHideDuration={10000}
        message={t("profile_no_longer_available", {
          gone: removedProfile?.gone,
          fallback: removedProfile?.fallback,
        })}
      />
      <ProfileContext.Provider value={{ profiles: freshProfiles, selectedProfileId: reconciledProfileId, ready: profilesReady }}>
      <TenantConfigContext.Provider value={tenantConfig}>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/share/:shareId" element={<ExternalShare />} />

            {/* Private Routes */}
            {(token && !user.isVisitor) && (
              <>
                <Route path="/" element={<ResponsiveLayout />}>
                  <Route index element={<MyProjects />} />
                  <Route path="/login/check" element={<RedirectToHome />} />
                  <Route
                    path="/projects/:projectId/:creationId/:versionNr"
                    element={<VersionedFile />}
                  />
                  <Route
                    path="/projects/:projectId/:creationId"
                    element={<VersionedFile />}
                  />
                  <Route path="/projects/:projectId" element={<ProjectDetail />} />
                  <Route path="/projects/" element={<MyProjects />} />
                  <Route path="/links/" element={<SharingLinks />} />
                  <Route
                    path="/admin"
                    element={<Stats />} />
                </Route>
                <Route path="/share/:shareId" element={<ExternalShare />} />
              </>
            )}
            {/* Default Redirect to Login Route */}
            <Route path="*" element={<LoginRedirect />} />
          </Routes>
        </Router>
      </TenantConfigContext.Provider >
      </ProfileContext.Provider>
    </>
  );
}


export default App;
